/**
 * ユーザー注文管理API
 */

const API_BASE_URL = import.meta.env.VITE_AISHA_API_BASE || 'http://localhost:7999/api';

// 注文ステータスの型定義
export type OrderStatus = 
  | 'draft'      // 下書き
  | 'pending'    // 確認待ち
  | 'failed'     // エラー
  | 'canceled'   // キャンセル
  | 'onhold'     // 保留中
  | 'inprocess'  // 製造中
  | 'partial'    // 一部発送済み
  | 'fulfilled'  // 発送済み
  | 'archived';  // アーカイブ済み

// 注文情報の型定義
export interface UserOrder {
  id: number;
  printful_order_id: string;
  status: OrderStatus;
  status_text: string;
  product_name: string;
  thumbnail_url?: string;
  quantity: number;
  total_cost: number;
  created_at: string;
  updated_at?: string;
  shipped_at?: string;
  tracking_number?: string;
  tracking_url?: string;
}

// 配送先情報の型定義
export interface RecipientInfo {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state_code?: string;
  state_name?: string;
  country_code?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

// 個別注文の型定義
export interface OrderDetail {
  order_id: string;
  status: OrderStatus;
  status_text: string;
  created_at?: string;
  shipped_at?: string;
  tracking_number?: string;
  tracking_url?: string;
  quantity: number;
  total_cost: number;
  recipient_info?: RecipientInfo;
}

// グッズ注文ステータスの型定義（複数注文対応）
export interface GoodsOrderStatus {
  has_order: boolean;
  orders?: OrderDetail[];  // 複数注文の配列
  // 後方互換性のため単一注文の情報も含む
  order_id?: string;
  status?: OrderStatus;
  status_text?: string;
  created_at?: string;
  shipped_at?: string;
  tracking_number?: string;
  tracking_url?: string;
  quantity?: number;
  total_cost?: number;
  recipient_info?: RecipientInfo;
}

// API レスポンスの型定義
export interface UserOrdersResponse {
  success: boolean;
  orders: UserOrder[];
  total_count: number;
}

export interface GoodsOrderStatusResponse {
  success: boolean;
  order: GoodsOrderStatus;
}

export interface SyncOrdersResponse {
  success: boolean;
  message: string;
  updated_count: number;
  error_count?: number;
}

class UserOrdersAPI {
  /**
   * ユーザーの注文一覧を取得
   */
  async getUserOrders(userId: string): Promise<UserOrdersResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/orders/?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get user orders: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to get user orders:', error);
      throw error;
    }
  }

  /**
   * 特定の商品の注文ステータスを取得
   */
  async getGoodsOrderStatus(userId: string, suzuriMerchandiseId: string): Promise<GoodsOrderStatusResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/orders/goods-status/?user_id=${userId}&suzuri_merchandise_id=${suzuriMerchandiseId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get goods order status: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to get goods order status:', error);
      throw error;
    }
  }

  /**
   * ユーザーの注文をPrintful APIと同期
   */
  async syncUserOrders(userId: string): Promise<SyncOrdersResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/user/orders/sync/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to sync user orders: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to sync user orders:', error);
      throw error;
    }
  }

  /**
   * 複数の商品の注文ステータスを一括取得
   */
  async getMultipleGoodsOrderStatus(
    userId: string, 
    suzuriMerchandiseIds: string[]
  ): Promise<Map<string, GoodsOrderStatus>> {
    try {
      // 新しいバックエンドAPIエンドポイントを使用
      const response = await fetch(`${API_BASE_URL}/user/orders/multiple-goods-status/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          suzuri_merchandise_ids: suzuriMerchandiseIds
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get multiple goods order status: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // レスポンスのstatus_mapをMapに変換
      const statusMap = new Map<string, GoodsOrderStatus>();
      if (data.success && data.status_map) {
        Object.entries(data.status_map).forEach(([merchandiseId, status]) => {
          statusMap.set(merchandiseId, status as GoodsOrderStatus);
        });
      }
      
      return statusMap;
    } catch (error) {
      console.error('Failed to get multiple goods order status:', error);
      throw error;
    }
  }

  /**
   * ステータスを日本語に変換
   */
  getStatusDisplayText(status: OrderStatus): string {
    const statusMap: Record<OrderStatus, string> = {
      'draft': '下書き',
      'pending': '確認待ち',
      'failed': 'エラー',
      'canceled': 'キャンセル',
      'onhold': '保留中',
      'inprocess': '製造中',
      'partial': '一部発送済み',
      'fulfilled': '発送済み',
      'archived': 'アーカイブ済み'
    };
    return statusMap[status] || status;
  }

  /**
   * ステータスの色を取得
   */
  getStatusColor(status: OrderStatus): string {
    const colorMap: Record<OrderStatus, string> = {
      'draft': 'gray',
      'pending': 'yellow',
      'failed': 'red',
      'canceled': 'gray',
      'onhold': 'orange',
      'inprocess': 'blue',
      'partial': 'indigo',
      'fulfilled': 'green',
      'archived': 'gray'
    };
    return colorMap[status] || 'gray';
  }
}

export const userOrdersAPI = new UserOrdersAPI();